import _slicedToArray from "@babel/runtime-corejs3/helpers/slicedToArray";
import { useCallback, useEffect, useLayoutEffect, useReducer, useRef } from 'react';
var isClient = typeof window !== 'undefined' && !/ServerSideRendering/.test(window.navigator && window.navigator.userAgent);
var useIsomorphicLayoutEffect = isClient ? useLayoutEffect : useEffect;

var useAbortSignal = function useAbortSignal() {
  var abortController = useRef();

  if (!abortController.current) {
    abortController.current = new AbortController();
  }

  useEffect(function () {
    var abort = function abort() {
      abortController.current.abort();
    };

    return abort;
  }, []);
  return abortController.current.signal;
};
/**
 * useReducer with async actions
 * @example
 * import { useReducerAsync } from 'use-reducer-async';
 *
 * const asyncActionHandlers = {
 *   SLEEP: ({ dispatch, getState, signal }) => async (action) => {
 *     dispatch({ type: 'START_SLEEP' });
 *     await new Promise(r => setTimeout(r, action.ms));
 *     dispatch({ type: 'END_SLEEP' });
 *   },
 *   FETCH: ({ dispatch, getState, signal }) => async (action) => {
 *     dispatch({ type: 'START_FETCH' });
 *     try {
 *       const response = await fetch(action.url);
 *       const data = await response.json();
 *       dispatch({ type: 'FINISH_FETCH', data });
 *     } catch (error) {
 *       dispatch({ type: 'ERROR_FETCH', error });
 *     }
 *   },
 * };
 * const [state, dispatch] = useReducerAsync(reducer, initialState, asyncActionHandlers);
 */


export function useReducerAsync(reducer, initializerArg, initializer, asyncActionHandlers) {
  var signal = useAbortSignal();
  var aaHandlers = asyncActionHandlers || initializer;

  var _useReducer = useReducer(reducer, initializerArg, asyncActionHandlers && initializer),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      state = _useReducer2[0],
      dispatch = _useReducer2[1];

  var lastState = useRef(state);
  useIsomorphicLayoutEffect(function () {
    lastState.current = state;
  }, [state]);
  var getState = useCallback(function () {
    return lastState.current;
  }, []);
  var wrappedDispatch = useCallback(function (action) {
    var _ref = action || {},
        type = _ref.type;

    var aaHandler = type && aaHandlers[type] || null;

    if (aaHandler) {
      return aaHandler({
        dispatch: wrappedDispatch,
        getState: getState,
        signal: signal
      })(action);
    } else {
      return dispatch(action);
    }
  }, [aaHandlers, getState, signal]);
  return [state, wrappedDispatch];
}